import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, CrmDashboardData, DashLead, Stage } from './crmApi';
import LeadPanel from './LeadPanel';
import { Empty, Head, Spinner, StageChip, fmtPhone, fmtWhen, inputCls, useCrmMe } from './ui';
import { C, Card, ReasonBars, Tile, barPath, dayLabel, named, nf, niceMax, useWidth } from './viz';

// ── Sales CRM dashboard ──────────────────────────────────────
// Managers see the whole team (or pick one person); a salesperson sees their
// own numbers. One filter row scopes everything below it.
// Chart colours: two validated series slots (blue = leads, orange = converted);
// text stays in text colours, never the series colour.

const RANGES = [{ key: 'today', label: 'Today' }, { key: '7d', label: '7 days' }, { key: '30d', label: '30 days' }, { key: 'month', label: 'This month' }];
const OPEN_STAGES: Stage[] = ['New', 'Not Picked', 'Call Later', 'Poll Sent', 'Interested', 'No Response', 'Future Prospect', 'Direct Visit'];

function fmtMins(m: number | null) {
  if (m === null || m === undefined) return '—';
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) return `${Math.round(m / 6) / 10} h`;
  return `${Math.round(m / 144) / 10} days`;
}
// The funnel: one series, one colour; the value at each bar's tip
const Funnel: React.FC<{ steps: { stage: string; n: number }[] }> = ({ steps }) => {
  const [ref, w] = useWidth<HTMLDivElement>();
  const top = Math.max(1, steps[0]?.n || 0);
  const labelW = Math.min(120, Math.max(86, w * 0.28));
  const valueW = 92;
  const trackW = Math.max(40, w - labelW - valueW);
  const rowH = 34, barH = 18;
  return (
    <div ref={ref} className="w-full">
      {w > 0 && (
        <svg width={w} height={steps.length * rowH} role="img" aria-label={steps.map(s => `${s.stage} ${s.n}`).join(', ')}>
          {steps.map((s, i) => {
            const y = i * rowH + (rowH - barH) / 2;
            const bw = Math.max(s.n > 0 ? 3 : 0, (s.n / top) * trackW);
            const share = i === 0 ? '' : ` · ${Math.round((s.n / top) * 100)}%`;
            return (
              <g key={s.stage}>
                <title>{`${s.stage}: ${nf(s.n)}${share}`}</title>
                <text x={0} y={y + barH / 2} dominantBaseline="middle" fontSize="12" fill={C.ink2}>{s.stage}</text>
                <rect x={labelW} y={y} width={trackW} height={barH} rx={4} fill={C.track} />
                <path d={barPath(labelW, y, bw, barH, true)} fill={C.leads} />
                <text x={labelW + bw + 8} y={y + barH / 2} dominantBaseline="middle" fontSize="12" fontWeight="700" fill={C.ink}>
                  {nf(s.n)}<tspan fontWeight="400" fill={C.muted}>{share}</tspan>
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
};

// Leads (added or given) and conversions per day: two series, one axis
const Trend: React.FC<{ data: CrmDashboardData['trend']; byWeek: boolean; leadsLabel: string }> = ({ data, byWeek, leadsLabel }) => {
  const [ref, w] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const H = 210, pad = { l: 34, r: 8, t: 10, b: 26 };
  const plotW = Math.max(10, w - pad.l - pad.r), plotH = H - pad.t - pad.b;
  const max = niceMax(Math.max(1, ...data.map(d => Math.max(d.leads, d.converted))));
  const ticks = [0, max / 2, max];
  const band = plotW / Math.max(1, data.length);
  const barW = Math.max(2, Math.min(16, (band - 6) / 2));
  const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 64))));
  const yOf = (v: number) => pad.t + plotH - (v / max) * plotH;
  const tip = hover !== null ? data[hover] : null;
  const tipLeft = hover !== null ? Math.min(Math.max(pad.l + hover * band + band / 2 - 70, 0), Math.max(0, w - 150)) : 0;
  const legend = (
    <div className="flex items-center gap-4 text-xs text-gray-600">
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: C.leads }} />{leadsLabel}</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: C.converted }} />Converted</span>
      <button onClick={() => setTable(t => !t)} className="text-purple-600 font-semibold ml-1">{table ? 'Show chart' : 'Show table'}</button>
    </div>
  );
  if (table) {
    return (
      <div className="space-y-3">
        {legend}
        <div className="overflow-x-auto max-h-72 border border-gray-100 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0"><tr>
              <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">{byWeek ? 'Week of' : 'Day'}</th>
              <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">{leadsLabel}</th>
              <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Converted</th>
            </tr></thead>
            <tbody>{data.map(d => (
              <tr key={d.day} className="border-t border-gray-100">
                <td className="px-3 py-1.5 text-gray-700">{dayLabel(d.day)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{nf(d.leads)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{nf(d.converted)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {legend}
      <div ref={ref} className="relative w-full" onMouseLeave={() => setHover(null)}>
        {w > 0 && (
          <svg width={w} height={H} role="img" aria-label={`${leadsLabel} and conversions ${byWeek ? 'per week' : 'per day'}`}>
            {ticks.map(t => (
              <g key={t}>
                <line x1={pad.l} x2={w - pad.r} y1={yOf(t)} y2={yOf(t)} stroke={t === 0 ? C.axis : C.grid} strokeWidth={1} />
                <text x={pad.l - 6} y={yOf(t)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill={C.muted} style={{ fontVariantNumeric: 'tabular-nums' }}>{nf(t)}</text>
              </g>
            ))}
            {data.map((d, i) => {
              const x0 = pad.l + i * band + (band - (barW * 2 + 2)) / 2;
              const on = hover === i;
              return (
                <g key={d.day} opacity={hover === null || on ? 1 : 0.55}>
                  <path d={barPath(x0, yOf(d.leads), barW, pad.t + plotH - yOf(d.leads))} fill={C.leads} />
                  <path d={barPath(x0 + barW + 2, yOf(d.converted), barW, pad.t + plotH - yOf(d.converted))} fill={C.converted} />
                  {i % every === 0 && (
                    <text x={pad.l + i * band + band / 2} y={H - 8} textAnchor="middle" fontSize="11" fill={C.muted}>{dayLabel(d.day)}</text>
                  )}
                  <rect x={pad.l + i * band} y={pad.t} width={band} height={plotH} fill="transparent" tabIndex={0}
                    aria-label={`${dayLabel(d.day)}: ${d.leads} ${leadsLabel.toLowerCase()}, ${d.converted} converted`}
                    onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} style={{ outline: 'none' }} />
                </g>
              );
            })}
          </svg>
        )}
        {tip && (
          <div className="absolute top-0 pointer-events-none bg-white border border-gray-200 shadow-lg rounded-xl px-3 py-2 text-xs w-[150px]" style={{ left: tipLeft }}>
            <p className="text-gray-500 mb-1">{byWeek ? `Week of ${dayLabel(tip.day)}` : dayLabel(tip.day)}</p>
            <p className="flex items-center gap-2"><span className="w-3 h-0.5" style={{ background: C.leads }} /><b className="text-gray-900">{nf(tip.leads)}</b><span className="text-gray-500">{leadsLabel.toLowerCase()}</span></p>
            <p className="flex items-center gap-2"><span className="w-3 h-0.5" style={{ background: C.converted }} /><b className="text-gray-900">{nf(tip.converted)}</b><span className="text-gray-500">converted</span></p>
          </div>
        )}
      </div>
    </div>
  );
};

const LeadList: React.FC<{ leads: DashLead[]; empty: string; onOpen: (id: string) => void; showOwner: boolean }> = ({ leads, empty, onOpen, showOwner }) => (
  leads.length === 0 ? <p className="text-sm text-gray-400">{empty}</p> : (
    <ul className="divide-y divide-gray-100">
      {leads.map(l => (
        <li key={l.id}>
          <button onClick={() => onOpen(l.id)} className="w-full text-left py-2 flex items-center justify-between gap-3 hover:bg-purple-50/40 rounded-lg px-1">
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-gray-800 truncate">{l.name || fmtPhone(l.phone)}</span>
              <span className="block text-[11px] text-gray-400 truncate">{[showOwner && l.ownerName, l.course].filter(Boolean).join(' · ')}</span>
            </span>
            <span className={`text-xs font-bold whitespace-nowrap ${l.nextAt && new Date(l.nextAt).getTime() < Date.now() ? 'text-red-600' : 'text-gray-700'}`}>{fmtWhen(l.nextAt)}</span>
          </button>
        </li>
      ))}
    </ul>
  )
);

// hideTeam: inside the Monitor, whose Team numbers has the per-person table
const CrmDashboard: React.FC<{ auth: CrmAuth; isManager: boolean; onNavigate?: (page: string) => void; embedded?: boolean; hideTeam?: boolean }> = ({ auth, isManager, embedded, hideTeam }) => {
  const { me } = useCrmMe(auth);
  const [range, setRange] = useState('7d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [person, setPerson] = useState('');
  const [data, setData] = useState<CrmDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const custom = range === 'custom' && from && to;
      setData(await crm.dashboard(auth, { range: custom ? undefined : range, from: custom ? from : undefined, to: custom ? to : undefined, salesPersonId: person || undefined }));
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [auth, range, from, to, person]);
  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => (me?.people || []).filter(p => p.takesLeads || (data?.people || []).some(x => x.id === p.id)), [me, data]);
  const team = isManager && !data?.scope.salesPersonId;
  const leadsLabel = team ? 'Leads added' : 'Leads given';

  if (!data) return loading ? <Spinner label="Loading the dashboard…" /> : <Empty icon="⚠️" title="Could not load the dashboard" />;
  const k = data.kpis;
  const openTotal = OPEN_STAGES.reduce((s, st) => s + (data.open[st] || 0), 0);
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title={isManager ? 'Sales dashboard' : 'My numbers'} onRefresh={load}
        sub={`${data.scope.name} · ${dayLabel(data.range.from)}${data.range.from !== data.range.to ? ` – ${dayLabel(data.range.to)}` : ''}`} />

      {/* One filter row scopes everything below it */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-white border border-gray-100">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)} className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={range === r.key ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>{r.label}</button>
          ))}
          <button onClick={() => setRange('custom')} className="px-3 py-1.5 rounded-lg text-xs font-bold"
            style={range === 'custom' ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>Dates…</button>
        </div>
        {range === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={`${inputCls} !py-1.5 !w-40`} aria-label="From" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={`${inputCls} !py-1.5 !w-40`} aria-label="To" />
          </div>
        )}
        {isManager && (
          <select value={person} onChange={e => setPerson(e.target.value)} className={`${inputCls} !py-1.5 !w-auto max-w-full`} aria-label="Salesperson">
            <option value="">Whole team</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <Tile label={leadsLabel} value={nf(team ? k.added : k.given)} sub={team ? `${nf(k.given)} handed out` : `${nf(k.movedAway)} moved on, not taken`} />
          <Tile label="Calls made" value={nf(k.calls)} />
          <Tile label="Talked to" value={nf(k.talked)} sub="leads, not counting Not Picked" />
          <Tile label="Brochures sent" value={nf(k.pitched)} sub="leads" />
          <Tile label="Polls sent" value={nf(k.pollsSent ?? 0)} sub="leads" />
          <Tile label="Interested" value={nf(k.interested ?? 0)} sub="said yes to the poll" />
          <Tile label="Converted" value={nf(k.converted)} sub="leads that paid" />
          <Tile label="Brochure → paid" value={`${k.conversionPct}%`} sub={`${nf(k.converted)} of ${nf(k.pitched)} sent a brochure`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <Card title="How far the leads got" sub={team ? 'Leads added in this range, and the furthest each got so far' : 'Leads given to them in this range, and the furthest they took each'} className="lg:col-span-2">
            <Funnel steps={data.funnel} />
          </Card>
          <Card title={data.byWeek ? 'Week by week' : 'Day by day'} className="lg:col-span-3">
            <Trend data={data.trend} byWeek={data.byWeek} leadsLabel={leadsLabel} />
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card title="Open right now" sub={`${nf(openTotal)} open leads${team && data.waiting ? ` · ${nf(data.waiting)} waiting for the next working morning or someone free` : ''}`}>
            <div className="flex flex-wrap gap-2">
              {OPEN_STAGES.map(st => (
                <span key={st} className="inline-flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                  <StageChip stage={st} small /><b className="text-gray-900 tabular-nums">{nf(data.open[st] || 0)}</b>
                </span>
              ))}
            </div>
          </Card>
          <Card title="Waiting for payment" sub="Interested leads, by the date they said they'd pay">
            <LeadList leads={data.pipeline} empty="Nobody is waiting to pay" onOpen={setOpen} showOwner={team} />
          </Card>
          <Card title="Visits today and tomorrow">
            <LeadList leads={data.visits} empty="No visits booked" onOpen={setOpen} showOwner={team} />
          </Card>
        </div>

        {team && !hideTeam && (
          <Card title="Team" sub="Per salesperson in this range. Click a row to see only their numbers.">
            {data.people.length === 0 ? <p className="text-sm text-gray-400">No one has leads yet.</p> : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm min-w-[900px]">
                  <thead><tr className="text-xs text-gray-500">
                    {['Salesperson', 'Given', 'Calls', 'Talked to', 'Brochures', 'Polls', 'Interested', 'Converted', 'Brochure → paid', 'Not taken', 'Typical first call'].map((h, i) => (
                      <th key={h} className={`py-2 px-2 font-bold ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data.people.map(p => (
                      <tr key={p.id} onClick={() => setPerson(p.id)} className="border-t border-gray-100 cursor-pointer hover:bg-purple-50/40">
                        <td className="py-2 px-2"><b className="text-gray-800">{p.name}</b>{!p.active && <span className="text-[10px] text-gray-400 ml-1">inactive</span>}<span className="block text-[11px] text-gray-400">{p.role}</span></td>
                        {[p.given, p.calls, p.talked, p.pitched, p.pollsSent ?? 0, p.interested ?? 0, p.converted].map((v, i) => <td key={i} className="py-2 px-2 text-right tabular-nums">{nf(v)}</td>)}
                        <td className="py-2 px-2 text-right tabular-nums">{p.pitched ? `${p.conversionPct}%` : '—'}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{nf(p.movedAway)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmtMins(p.firstCallMins)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {team && (
            <Card title="Lead sources" sub="Leads added in this range">
              {data.sources.length === 0 ? <p className="text-sm text-gray-400">No leads added in this range.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-gray-500"><th className="text-left py-1.5 font-bold">Source</th><th className="text-right font-bold">Added</th><th className="text-right font-bold">Talked</th><th className="text-right font-bold">Converted</th></tr></thead>
                  <tbody>{data.sources.map(s => (
                    <tr key={s.source} className="border-t border-gray-100">
                      <td className="py-1.5 text-gray-700">{named(s.source)}</td>
                      <td className="text-right tabular-nums">{nf(s.added)}</td>
                      <td className="text-right tabular-nums">{nf(s.talked)}</td>
                      <td className="text-right tabular-nums">{nf(s.converted)}{s.added ? <span className="text-gray-400 text-[11px]"> · {Math.round((s.converted / s.added) * 100)}%</span> : null}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
              {data.types.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">WhatsApp or form</p>
                  <table className="w-full text-sm">
                    <tbody>{data.types.map(t => (
                      <tr key={t.leadType} className="border-t border-gray-100 first:border-0">
                        <td className="py-1.5 text-gray-700">{t.leadType === 'WhatsApp' ? 'WhatsApp leads' : t.leadType === 'Form' ? 'Form leads' : 'Type not set'}</td>
                        <td className="text-right tabular-nums">{nf(t.added)} added</td>
                        <td className="text-right tabular-nums">{nf(t.talked)} talked</td>
                        <td className="text-right tabular-nums">{nf(t.converted)} converted{t.added ? <span className="text-gray-400 text-[11px]"> · {Math.round((t.converted / t.added) * 100)}%</span> : null}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
              {data.courses.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100 space-y-1">
                  {data.courses.map(c => (
                    <p key={c.course} className="text-xs text-gray-600 flex justify-between"><span>{named(c.course)}</span><span className="tabular-nums"><b className="text-gray-900">{nf(c.added)}</b> added · {nf(c.converted)} converted</span></p>
                  ))}
                </div>
              )}
            </Card>
          )}
          <Card title="Why Cold Lead" sub="Calls in this range">
            <ReasonBars rows={data.reasons.cold} empty="No cold leads in this range" />
          </Card>
          <Card title="Why Not Interested" sub="Poll answers in this range">
            <ReasonBars rows={data.reasons.notInterested} empty="No Not Interested in this range" />
          </Card>
        </div>
      </div>

      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default CrmDashboard;
